import type { JSX, ReactNode } from 'react'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined'
import { ApiError } from '../api'

/**
 * Every list in this product can legitimately be empty — a new account, a job
 * that found nobody, a filter that excludes everything — so an empty result is a
 * designed state with an explanation, never a blank rectangle.
 */
export function EmptyState({
  title,
  description,
  action,
  icon,
  dense = false,
}: Readonly<{
  title: string
  description?: string
  action?: ReactNode
  icon?: ReactNode
  dense?: boolean
}>): JSX.Element {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={1}
      sx={{ px: 3, py: dense ? 3 : 6, textAlign: 'center', height: '100%' }}
    >
      <Box sx={{ color: 'text.disabled', display: 'flex' }}>
        {icon ?? <InboxOutlinedIcon fontSize="large" />}
      </Box>
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
      {description ? (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460 }}>
          {description}
        </Typography>
      ) : null}
      {action ? <Box sx={{ pt: 1 }}>{action}</Box> : null}
    </Stack>
  )
}

export function LoadingPanel({ label = 'Loading' }: Readonly<{ label?: string }>): JSX.Element {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ py: 8 }}>
      <CircularProgress size={28} />
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  )
}

function describe(error: unknown): { title: string; message: string } {
  if (error instanceof ApiError) {
    if (error.status === 0) return { title: 'Cannot reach the API', message: error.message }
    if (error.status === 403) return { title: 'Not permitted', message: error.message }
    return { title: `Request failed (${error.status})`, message: error.message }
  }
  if (error instanceof Error) return { title: 'Something went wrong', message: error.message }
  return { title: 'Something went wrong', message: String(error) }
}

export function ErrorPanel({
  error,
  onRetry,
}: Readonly<{
  error: unknown
  onRetry?: () => void
}>): JSX.Element {
  const { title, message } = describe(error)
  return (
    <Alert
      severity="error"
      sx={{ my: 2 }}
      action={
        onRetry ? (
          <Button color="inherit" size="small" onClick={onRetry}>
            Retry
          </Button>
        ) : undefined
      }
    >
      <AlertTitle>{title}</AlertTitle>
      {message}
    </Alert>
  )
}
